import { MontoCRC } from '@/components/dominio';
import { VendorPickerBC } from '@/components/filtros/VendorPickerBC';
import { ImportarProveedoresBCDialog } from '@/components/flujo-semanal/ImportarProveedoresBCDialog';
import { ImportarReferenciasBACDialog } from '@/components/flujo-semanal/ImportarReferenciasBACDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useActualizarProveedor, useCrearProveedor, useProveedores } from '@/hooks/useFlujo';
import { useSincronizarPagos } from '@/hooks/useSincronizarPagos';
import { cliente } from '@/lib/cliente';
import { colorDeIban } from '@/lib/coloresBanco';
import type { Proveedor } from '@adelante/shared';
import { NOMBRE_BANCO_POR_CODIGO, codigoBancoDeIban, nombreBancoDeIban } from '@adelante/shared';
import { Building2, Edit, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

type CuentaForm = {
  id?: number;
  moneda: 'C' | 'D';
  iban: string;
  banco: string;
  tipoCuenta: string;
  predeterminada: boolean;
};

type FormData = {
  nombre: string;
  cedula: string;
  cuentas: CuentaForm[];
  notas: string;
  vendorCode: string | null;
  esEmpresaRelacionada: boolean;
};

const FORM_VACIO: FormData = {
  nombre: '',
  cedula: '',
  cuentas: [],
  notas: '',
  vendorCode: null,
  esEmpresaRelacionada: false,
};

const CUENTA_VACIA: CuentaForm = {
  moneda: 'C',
  iban: '',
  banco: '',
  tipoCuenta: '',
  predeterminada: false,
};

function DialogProveedor({
  proveedor,
  inicial,
  open,
  onClose,
}: {
  proveedor?: Proveedor;
  /** Para un proveedor nuevo que llega desde otra pantalla: proveedor de BC, nombre y moneda de su primera cuenta. */
  inicial?: { vendorCode: string; nombre: string; moneda: 'C' | 'D' } | null;
  open: boolean;
  onClose: () => void;
}) {
  const [form, setForm] = useState<FormData>(FORM_VACIO);

  // El diálogo queda montado todo el tiempo (solo se muestra/oculta con `open`), así que el
  // formulario no se puede llenar solo en el useState inicial — hay que resincronizarlo cada
  // vez que se abre para editar un proveedor distinto.
  useEffect(() => {
    if (!open) return;
    setForm(
      proveedor
        ? {
            nombre: proveedor.nombre,
            cedula: proveedor.cedula ?? '',
            cuentas: proveedor.cuentas.map((c) => ({
              id: c.id,
              moneda: c.moneda,
              iban: c.iban,
              banco: c.banco ?? '',
              tipoCuenta: c.tipoCuenta ?? '',
              predeterminada: c.predeterminada,
            })),
            notas: proveedor.notas ?? '',
            vendorCode: proveedor.vendorCode,
            esEmpresaRelacionada: proveedor.esEmpresaRelacionada,
          }
        : inicial
          ? {
              ...FORM_VACIO,
              nombre: inicial.nombre,
              vendorCode: inicial.vendorCode,
              cuentas: [{ ...CUENTA_VACIA, moneda: inicial.moneda }],
            }
          : FORM_VACIO,
    );
  }, [open, proveedor, inicial]);

  const { mutate: crear, isPending: creando } = useCrearProveedor();
  const { mutate: actualizar, isPending: actualizando } = useActualizarProveedor();
  const isPending = creando || actualizando;

  const set = (k: 'nombre' | 'cedula' | 'notas') => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  // Lo último que se copió de BC: solo se reemplaza un campo si sigue igual (no lo editaron a mano).
  const copiado = useRef({ nombre: '', cedula: '' });
  async function copiarDeBC(vendorCode: string) {
    try {
      const r = await cliente.bcVendors.detalle(vendorCode);
      const bc = r.data;
      if (!bc) return;
      setForm((f) => {
        const siguiente = { ...f };
        if (bc.nombre && (!f.nombre.trim() || f.nombre === copiado.current.nombre)) {
          siguiente.nombre = bc.nombre;
          copiado.current.nombre = bc.nombre;
        }
        if (bc.cedula && (!f.cedula.trim() || f.cedula === copiado.current.cedula)) {
          siguiente.cedula = bc.cedula;
          copiado.current.cedula = bc.cedula;
        }
        return siguiente;
      });
    } catch {
      toast.error('No se pudieron leer los datos del proveedor en BC');
    }
  }

  const cambiarCuenta = (i: number, cambios: Partial<CuentaForm>) =>
    setForm((f) => ({
      ...f,
      cuentas: f.cuentas.map((c, j) => {
        if (j === i) return { ...c, ...cambios };
        // Una sola predeterminada por moneda: al marcar una, se desmarcan las demás de esa moneda.
        if (cambios.predeterminada && c.moneda === (cambios.moneda ?? f.cuentas[i]?.moneda)) {
          return { ...c, predeterminada: false };
        }
        return c;
      }),
    }));

  const handleGuardar = () => {
    if (!form.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    const payload = {
      nombre: form.nombre.trim(),
      cedula: form.cedula || null,
      cuentas: form.cuentas
        .filter((c) => c.iban.trim())
        .map((c) => ({
          id: c.id,
          moneda: c.moneda,
          iban: c.iban,
          banco: c.banco.trim() || null,
          tipoCuenta: c.tipoCuenta.trim() || null,
          predeterminada: c.predeterminada,
        })),
      notas: form.notas || null,
      vendorCode: form.vendorCode,
      esEmpresaRelacionada: form.esEmpresaRelacionada,
    };

    if (proveedor) {
      actualizar(
        { id: proveedor.id, ...payload },
        {
          onSuccess: () => {
            toast.success('Proveedor actualizado');
            onClose();
          },
          onError: (e) => toast.error(motivo(e, 'No se pudo actualizar el proveedor')),
        },
      );
    } else {
      crear(payload, {
        onSuccess: () => {
          toast.success('Proveedor creado');
          onClose();
        },
        onError: (e) => toast.error(motivo(e, 'No se pudo crear el proveedor')),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} modal={false}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{proveedor ? 'Editar proveedor' : 'Nuevo proveedor'}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="space-y-1">
            <Label>Proveedor en Business Central</Label>
            <VendorPickerBC
              enLinea
              value={form.vendorCode}
              onChange={(vendorCode) => {
                setForm((f) => ({ ...f, vendorCode }));
                if (vendorCode) copiarDeBC(vendorCode);
              }}
            />
            <p className="text-xs text-muted-foreground">
              Al elegirlo se copian el nombre y la cédula de BC (si los tiene y están vacíos o no se
              han cambiado a mano). Las cuentas de banco no están disponibles en BC.
            </p>
            {proveedor?.vendorNombreBC && (
              <p className="text-xs text-muted-foreground">
                Pendiente en BC:{' '}
                <MontoCRC monto={proveedor.pendienteBC ?? 0} className="font-medium" />
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label>Nombre / Razón social *</Label>
            <Input
              value={form.nombre}
              onChange={set('nombre')}
              placeholder="Nombre del proveedor"
            />
          </div>
          <div className="space-y-1">
            <Label>Cédula</Label>
            <Input value={form.cedula} onChange={set('cedula')} placeholder="3-101-XXXXXX" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Cuentas de pago</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    cuentas: [
                      ...f.cuentas,
                      {
                        ...CUENTA_VACIA,
                        // La primera cuenta de cada moneda es la predeterminada.
                        predeterminada: !f.cuentas.some((c) => c.moneda === 'C'),
                      },
                    ],
                  }))
                }
              >
                <Plus className="h-4 w-4" />
                Agregar cuenta
              </Button>
            </div>
            {form.cuentas.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Sin cuentas. Agregá una por cada moneda en que se le paga (colones o dólares); si
                tiene varias en la misma moneda, marcá cuál es la predeterminada.
              </p>
            )}
            {form.cuentas.map((c, i) => (
              <div
                key={c.id ?? `nueva-${i}`}
                className="space-y-2 rounded-md border border-border p-3"
              >
                <div className="grid grid-cols-[120px_1fr_auto] items-end gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Moneda</Label>
                    <Select
                      value={c.moneda}
                      onValueChange={(v) => cambiarCuenta(i, { moneda: v as 'C' | 'D' })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="C">Colones (₡)</SelectItem>
                        <SelectItem value="D">Dólares ($)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">IBAN</Label>
                    <Input
                      value={c.iban}
                      onChange={(e) => {
                        const iban = e.target.value;
                        // El banco sale del código del IBAN: se completa solo cuando se reconoce.
                        const codigo = codigoBancoDeIban(iban);
                        const banco = codigo ? NOMBRE_BANCO_POR_CODIGO[codigo] : undefined;
                        cambiarCuenta(i, banco ? { iban, banco } : { iban });
                      }}
                      placeholder="CR00000000000000000000"
                      className="font-mono"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9"
                    title="Quitar esta cuenta"
                    onClick={() =>
                      setForm((f) => ({ ...f, cuentas: f.cuentas.filter((_, j) => j !== i) }))
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Nombre del banco (referencia)</Label>
                    <Select
                      value={c.banco || SIN_BANCO}
                      onValueChange={(v) => cambiarCuenta(i, { banco: v === SIN_BANCO ? '' : v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Elegir banco" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SIN_BANCO}>Sin indicar</SelectItem>
                        {opcionesBancos(c.banco).map((b) => (
                          <SelectItem key={b} value={b}>
                            {b}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Tipo de cuenta</Label>
                    <Input
                      value={c.tipoCuenta}
                      onChange={(e) => cambiarCuenta(i, { tipoCuenta: e.target.value })}
                      placeholder="Corriente"
                    />
                  </div>
                  <label className="flex h-9 items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={c.predeterminada}
                      onChange={(e) => cambiarCuenta(i, { predeterminada: e.target.checked })}
                    />
                    Predeterminada
                  </label>
                </div>
              </div>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.esEmpresaRelacionada}
              onChange={(e) => setForm((f) => ({ ...f, esEmpresaRelacionada: e.target.checked }))}
            />
            Empresa relacionada (se elige en su propio desplegable al crear un concepto)
          </label>
          <div className="space-y-1">
            <Label>Notas</Label>
            <Input
              value={form.notas}
              onChange={set('notas')}
              placeholder="Observaciones opcionales"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={isPending}>
            {isPending ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SIN_CUENTA = '__sin_cuenta__';
const SIN_BANCO = '__sin_banco__';

/** Bancos y entidades del listado oficial; si la cuenta trae un nombre antiguo (texto libre) se conserva. */
function opcionesBancos(actual: string): string[] {
  const nombres = [...new Set(Object.values(NOMBRE_BANCO_POR_CODIGO))].sort((a, b) =>
    a.localeCompare(b, 'es'),
  );
  return actual && !nombres.includes(actual) ? [actual, ...nombres] : nombres;
}

/** El mensaje que devolvió la API (permiso, IBAN inválido o repetido, ...) y, si no hay, uno genérico. */
function motivo(e: unknown, generico: string): string {
  return e instanceof Error && e.message ? e.message : generico;
}

export function ProveedoresPantalla() {
  const { data: proveedores, isLoading } = useProveedores();
  const sincronizar = useSincronizarPagos();

  const actualizarDesdeBC = () =>
    sincronizar.mutate(undefined, {
      onSuccess: (r) => {
        if (!r.data) {
          toast.error(r.error?.mensaje ?? 'No se pudo actualizar desde Business Central');
          return;
        }
        const nuevos = r.data.proveedoresCreados;
        toast.success(
          nuevos > 0
            ? `Actualizado desde BC: ${nuevos} proveedor(es) nuevo(s) en la app`
            : 'Actualizado desde BC: no hay proveedores nuevos',
        );
      },
      onError: () => toast.error('No se pudo actualizar desde Business Central'),
    });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState<Proveedor | undefined>();
  const [busqueda, setBusqueda] = useState('');
  const [dialogImportarAbierto, setDialogImportarAbierto] = useState(false);
  const [dialogBCAbierto, setDialogBCAbierto] = useState(false);
  const [soloSinCedula, setSoloSinCedula] = useState(false);
  const [soloRelacionadas, setSoloRelacionadas] = useState(false);
  const [bancoFiltro, setBancoFiltro] = useState('');
  const [params, setParams] = useSearchParams();
  const [inicial, setInicial] = useState<{
    vendorCode: string;
    nombre: string;
    moneda: 'C' | 'D';
  } | null>(null);

  // Llegada desde una lista de pago: ?vendorCode=…&nombre=…&moneda=C|D abre el proveedor para editarlo
  // (agregar la cuenta que falta) o, si no existe, el formulario de uno nuevo ya con sus datos.
  const vendorCodeParam = params.get('vendorCode');
  useEffect(() => {
    if (!vendorCodeParam || !proveedores) return;
    const existente = proveedores.find((p) => p.vendorCode === vendorCodeParam);
    const moneda = params.get('moneda') === 'D' ? 'D' : 'C';
    if (existente) {
      setEditando(existente);
      setInicial(null);
    } else {
      setEditando(undefined);
      setInicial({ vendorCode: vendorCodeParam, nombre: params.get('nombre') ?? '', moneda });
    }
    setDialogOpen(true);
    setParams({}, { replace: true });
  }, [vendorCodeParam, proveedores, params, setParams]);

  const { mutate: actualizar } = useActualizarProveedor();

  const sinCedula = (p: Proveedor) => (p.cedula ?? '').replace(/\D/g, '').length < 9;
  const cantidadSinCedula = proveedores?.filter((p) => p.activo && sinCedula(p)).length ?? 0;

  const codigosDe = (p: Proveedor) =>
    new Set(p.cuentas.map((c) => codigoBancoDeIban(c.iban) ?? 'otro'));

  // Opciones del filtro por banco: solo los bancos que hoy tienen cuentas, con cuántos proveedores.
  const opcionesBanco = (() => {
    const cuenta = new Map<string, { nombre: string; cantidad: number }>();
    let sinCuenta = 0;
    for (const p of proveedores ?? []) {
      if (p.cuentas.length === 0) sinCuenta++;
      for (const codigo of codigosDe(p)) {
        const nombre =
          codigo === 'otro'
            ? 'IBAN no reconocido'
            : (NOMBRE_BANCO_POR_CODIGO[codigo] ?? `Banco ${codigo}`);
        cuenta.set(codigo, { nombre, cantidad: (cuenta.get(codigo)?.cantidad ?? 0) + 1 });
      }
    }
    return {
      sinCuenta,
      bancos: [...cuenta.entries()].sort((a, b) => b[1].cantidad - a[1].cantidad),
    };
  })();

  const filtrados =
    proveedores?.filter(
      (p) =>
        (p.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
          (p.cedula ?? '').includes(busqueda)) &&
        (!soloSinCedula || sinCedula(p)) &&
        (!soloRelacionadas || p.esEmpresaRelacionada) &&
        (bancoFiltro === ''
          ? true
          : bancoFiltro === SIN_CUENTA
            ? p.cuentas.length === 0
            : codigosDe(p).has(bancoFiltro)),
    ) ?? [];

  const handleDesactivar = (p: Proveedor) => {
    actualizar(
      { id: p.id, activo: !p.activo },
      { onSuccess: () => toast.success(p.activo ? 'Proveedor desactivado' : 'Proveedor activado') },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div>
          <h1 className="text-xl font-bold">Proveedores</h1>
          <p className="text-sm text-muted-foreground">
            {proveedores?.length ?? 0} proveedores registrados
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Input
            placeholder="Buscar por nombre o cédula..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-64"
          />
          <select
            aria-label="Filtrar por banco"
            value={bancoFiltro}
            onChange={(e) => setBancoFiltro(e.target.value)}
            className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
          >
            <option value="">Todos los bancos</option>
            {opcionesBanco.bancos.map(([codigo, b]) => (
              <option key={codigo} value={codigo}>
                {b.nombre} ({b.cantidad})
              </option>
            ))}
          </select>
          <Button
            variant={bancoFiltro === SIN_CUENTA ? 'default' : 'outline'}
            aria-pressed={bancoFiltro === SIN_CUENTA}
            onClick={() => setBancoFiltro((v) => (v === SIN_CUENTA ? '' : SIN_CUENTA))}
            title="Proveedores sin ninguna cuenta bancaria registrada"
          >
            Sin cuenta ({opcionesBanco.sinCuenta})
          </Button>
          <Button
            variant={soloRelacionadas ? 'default' : 'outline'}
            aria-pressed={soloRelacionadas}
            onClick={() => setSoloRelacionadas((v) => !v)}
            title="Solo las empresas relacionadas (ej. Adelante Servicios)"
          >
            Empresas relacionadas ({proveedores?.filter((p) => p.esEmpresaRelacionada).length ?? 0})
          </Button>
          <Button
            variant={soloSinCedula ? 'default' : 'outline'}
            aria-pressed={soloSinCedula}
            onClick={() => setSoloSinCedula((v) => !v)}
            title="Proveedores sin cédula: el Banco Nacional la exige para pagarles"
          >
            Sin cédula ({cantidadSinCedula})
          </Button>
          <Button
            variant="outline"
            onClick={() => setDialogImportarAbierto(true)}
            className="gap-2"
          >
            Importar CSV de BAC
          </Button>
          <Button
            variant="outline"
            onClick={actualizarDesdeBC}
            disabled={sincronizar.isPending}
            className="gap-2"
            title="Trae de Business Central los proveedores y movimientos nuevos y crea en la app los proveedores que falten. Tarda un par de minutos; además corre sola todos los días a las 9 pm."
          >
            <RefreshCw className={`h-4 w-4 ${sincronizar.isPending ? 'animate-spin' : ''}`} />
            {sincronizar.isPending ? 'Actualizando…' : 'Actualizar desde BC'}
          </Button>
          <Button
            variant="outline"
            onClick={() => setDialogBCAbierto(true)}
            title="Crear los proveedores de Business Central con movimiento en los últimos 6 meses"
          >
            Importar de BC
          </Button>
          <Button
            onClick={() => {
              setEditando(undefined);
              setDialogOpen(true);
            }}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Nuevo
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground text-sm">Cargando...</div>
      ) : (
        <div className="border rounded-lg divide-y">
          {filtrados.length === 0 && (
            <div className="p-8 text-center text-muted-foreground text-sm">
              {busqueda ? 'Sin resultados para esa búsqueda.' : 'No hay proveedores registrados.'}
            </div>
          )}
          {filtrados.map((p) => (
            <div
              key={p.id}
              className={`flex items-center gap-4 p-3 ${!p.activo ? 'opacity-50' : ''}`}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{p.nombre}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                    {p.cedula ? (
                      <span className="font-mono">{p.cedula}</span>
                    ) : (
                      <Badge
                        variant="outline"
                        className="border-amber-300 py-0 text-xs text-amber-700"
                        title="El Banco Nacional exige la cédula del beneficiario"
                      >
                        Sin cédula
                      </Badge>
                    )}
                    {p.vendorCode && (
                      <Badge variant="success" className="text-xs py-0">
                        BC: {p.vendorCode}
                        {p.pendienteBC ? (
                          <>
                            {' · '}
                            <MontoCRC monto={p.pendienteBC} />
                          </>
                        ) : null}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              {p.cuentas.length > 0 ? (
                <div className="flex shrink-0 flex-col gap-0.5">
                  {p.cuentas.map((c) => {
                    const color = colorDeIban(c.iban);
                    return (
                      <div
                        key={c.id}
                        className={`flex items-center gap-1 rounded border px-2 py-0.5 text-xs ${color.borde} ${color.suave}`}
                        title={[c.banco, c.tipoCuenta, c.predeterminada ? 'predeterminada' : null]
                          .filter(Boolean)
                          .join(' · ')}
                      >
                        <span>{c.moneda === 'C' ? '₡' : '$'}</span>
                        <span className="font-mono">{c.iban}</span>
                        {/* El banco real sale del código del IBAN: el texto libre no es confiable. */}
                        <span
                          className={`rounded px-1 font-medium ${color.activo}`}
                          title="Banco según el código del IBAN"
                        >
                          {nombreBancoDeIban(c.iban) ?? 'Otro banco'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Badge variant="outline" className="text-xs text-muted-foreground shrink-0">
                  Sin IBAN
                </Badge>
              )}
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => {
                    setEditando(p);
                    setDialogOpen(true);
                  }}
                >
                  <Edit className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground"
                  onClick={() => handleDesactivar(p)}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <DialogProveedor
        proveedor={editando}
        inicial={inicial}
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditando(undefined);
          setInicial(null);
        }}
      />
      <ImportarProveedoresBCDialog
        open={dialogBCAbierto}
        onClose={() => setDialogBCAbierto(false)}
      />
      <ImportarReferenciasBACDialog
        open={dialogImportarAbierto}
        onClose={() => setDialogImportarAbierto(false)}
      />
    </div>
  );
}
